import api from "../../api";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../css/quill-fonts.css";
import AuthContext from "../../context/AuthContext";
import { Link } from "react-router-dom";
import React, { useState, useEffect, useContext, useRef } from "react";
  
/* ----------------------------------------
   🔥 Register Custom Fonts (Same as Blog/Admin Pages)
---------------------------------------- */
const Font = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "times-new-roman",
  "georgia",
  "courier-new",
  "verdana",
];
Quill.register(Font, true);

/* ----------------------------------------
   🔥 Full Quill Toolbar
---------------------------------------- */
const toolbarOptions = [
  [{ font: ["arial", "times-new-roman", "georgia", "courier-new", "verdana"] }],
  [{ size: ["small", false, "large", "huge"] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ["blockquote", "code-block"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ script: "sub" }, { script: "super" }],
  ["link", "image", "video"],
  ["clean"],
];

const modules = { toolbar: { container: toolbarOptions } };

const formats = [
  "font",
  "size",
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "blockquote",
  "code-block",
  "list",
  "bullet",
  "script",
  "link",
  "image",
  "video",
];

const presetHeadings = ["Introduction", "Discussion", "Conclusion", "References"];

/* ----------------------------------------
   MAIN COMPONENT
---------------------------------------- */
const Research = () => {
  const { user } = useContext(AuthContext);

  const [title, setTitle] = useState("");
  const [abstractText, setAbstractText] = useState("");
  const [keywords, setKeywords] = useState("");
  const [sections, setSections] = useState([]);
  const [customHeading, setCustomHeading] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
const [digitalSignature, setDigitalSignature] = useState(false);
  const [pdfs, setPdfs] = useState({
  universityApproval: null,
  iecClearance: null,
  noc: null,
  finalResults: null,
  licensingAgreement: null,
});
const universityApprovalRef = useRef(null);
const iecClearanceRef = useRef(null);
const nocRef = useRef(null);
const finalResultsRef = useRef(null);
const licensingAgreementRef = useRef(null);

  const [researchers, setResearchers] = useState([{ name: "", qualification: "" }]);
  const [papers, setPapers] = useState([]);
const removeSection = (index) => {

  setSections(prev => prev.filter((_, i) => i !== index));
};
/* ----------------------------------------
     Fetch Papers
  ---------------------------------------- */
 const fetchPapers = async () => {
  try {
    const res = await api.get("/api/research-papers");

    // ✅ FILTER ONLY APPROVED PAPERS
    const approvedOnly = (res.data || []).filter(
      (paper) => paper.status === "APPROVED"
    );

    setPapers(approvedOnly);
  } catch (err) {
    console.error("Error fetching papers:", err);
  }
};

const validateAndSetPdf = (key, file, inputRef) => {
  if (!file) return;

  if (file.type !== "application/pdf") {
    alert("Only PDF files allowed");
    if (inputRef?.current) inputRef.current.value = "";
    return;
  }

  const sizeKB = file.size / 1024;
  if (sizeKB < 50 || sizeKB > 100) {
    alert("PDF must be between 50KB and 100KB");
    if (inputRef?.current) inputRef.current.value = "";
    return;
  }

  setPdfs(prev => ({ ...prev, [key]: file }));
};

const downloadTemplate = async (type) => {
  try {
    const response = await api.get(
      `/api/research-papers/templates/${type}`,
      { responseType: "blob" }
    );

    // ✅ DO NOT FORCE PDF
    const blob = new Blob([response.data]);

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download =
      type === "noc"
        ? "NOC_Template.docx"
        : "Licensing_Template.docx";

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Template download failed", err);
    alert("Failed to download template");
  }
};

  useEffect(() => {
    fetchPapers();
  }, []);

  /* ----------------------------------------
     Add Section
  ---------------------------------------- */
  const addSection = (heading) => {
    setSections([...sections, { heading, content: "" }]);
  };

  /* ----------------------------------------
     Add Researcher
  ---------------------------------------- */
  const addResearcher = () => {
    setResearchers([...researchers, { name: "", qualification: "" }]);
  };
const removeResearcher = (index) => {
  setResearchers(prev => prev.filter((_, i) => i !== index));
};

  /* ----------------------------------------
     Submit Research Paper
  ---------------------------------------- */
 const handleSubmit = async (e) => {
  e.preventDefault();

  const email = user?.username;
  const userType = user?.customerType;

  if (!email || !userType) {
    alert("User details missing. Login again.");
    return;
  }

for (let key in pdfs) {
  if (!pdfs[key]) {
    alert("Please upload all required documents");
    return;
  }
}


  const paper = {
    title,
    abstractText,
    keywords,
    sectionsJson: JSON.stringify(sections),
    researchersJson: JSON.stringify(researchers),
  };

  const formData = new FormData();

  // 🔥 BACKEND EXPECTS STRING JSON
  formData.append("paper", JSON.stringify(paper));

  // 🔥 BACKEND EXPECTS key = files
formData.append("universityApproval", pdfs.universityApproval);
formData.append("iecClearance", pdfs.iecClearance);
formData.append("noc", pdfs.noc);
formData.append("finalResults", pdfs.finalResults);
formData.append("licensingAgreement", pdfs.licensingAgreement);

if (!acceptTerms) {
  alert("You must accept the Terms and Conditions to proceed.");
  return;
}

if (!digitalSignature) {
  alert("You must provide Digital Signature approval.");
  return;
}

  try {
    await api.post(
      "/api/research-papers/customers",
      formData,
      {
        params: { email, userType },
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    alert("Research Paper Submitted! Awaiting Approval.");

    // RESET
    setTitle("");
    setAbstractText("");
    setKeywords("");
    setSections([]);
    setResearchers([{ name: "", qualification: "" }]);
    setCustomHeading(null);
    setPdfs([]);

    fetchPapers();
  } catch (err) {
    console.error("Error submitting paper:", err);
    alert("Submission failed. Please try again.");
  }
};



  return (
    <div className="container mt-4">
      <h2>Add Research Paper</h2>

      <Link to="/customer/customerroleresearch" className="btn btn-primary float-end">
        My Research Papers
      </Link>
      <br />

      {/* ----------------------------------------
          FORM
      ---------------------------------------- */}
      <form onSubmit={handleSubmit}>
        <label>Title</label>
        <input
          type="text"
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        {/* ABSTRACT */}
        <label className="mt-3">Abstract</label>
        <ReactQuill
          theme="snow"
          value={abstractText}
          onChange={setAbstractText}
          modules={modules}
          formats={formats}
        />

        {/* KEYWORDS */}
        <label className="mt-3">Keywords</label>
        <input
          type="text"
          className="form-control"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
        />

        {/* RESEARCHERS */}
        <h4 className="mt-4">Researchers</h4>

    {researchers.map((resItem, idx) => (
  <div
    key={idx}
    className="mb-3"
    style={{ position: "relative" }}
  >
    {/* ❌ REMOVE RESEARCHER */}
    <button
      type="button"
      onClick={() => removeResearcher(idx)}
      style={{
        position: "absolute",
        right: "-10px",
        top: "-10px",
        background: "red",
        color: "white",
        border: "none",
        borderRadius: "50%",
        width: "24px",
        height: "24px",
        cursor: "pointer",
        fontWeight: "bold",
        lineHeight: "1",
      }}
    >
      ✕
    </button>

    <input
      type="text"
      placeholder="Name"
      className="form-control mb-1"
      value={resItem.name}
      onChange={(e) => {
        const updated = [...researchers];
        updated[idx].name = e.target.value;
        setResearchers(updated);
      }}
    />

    <input
      type="text"
      placeholder="Qualification & Institution"
      className="form-control"
      value={resItem.qualification}
      onChange={(e) => {
        const updated = [...researchers];
        updated[idx].qualification = e.target.value;
        setResearchers(updated);
      }}
    />
  </div>
))}

        <button type="button" className="btn btn-secondary mb-3" onClick={addResearcher}>
          Add Researcher
        </button>

        {/* SECTIONS */}
        <h4 className="mt-4">Sections</h4>

     {sections.map((sec, idx) => (
  <div
    key={idx}
    className="border rounded p-3 mb-4"
    style={{ position: "relative" }}
  >
    {/* HEADER ROW */}
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
      }}
    >
      <h5 className="fw-bold m-0">{sec.heading}</h5>

      {/* ❌ REMOVE SECTION */}
      <button
        type="button"
        onClick={() => removeSection(idx)}
        style={{
          background: "red",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "24px",
          height: "24px",
          cursor: "pointer",
          fontWeight: "bold",
          lineHeight: "1",
        }}
      >
        ✕
      </button>
    </div>

    {/* QUILL EDITOR */}
    <ReactQuill
      theme="snow"
      value={sec.content}
      onChange={(val) => {
        const updated = [...sections];
        updated[idx].content = val;
        setSections(updated);
      }}
      modules={modules}
      formats={formats}
    />
  </div>
))}
        {customHeading !== null && (
          <div className="my-3">
            <input
              type="text"
              placeholder="Enter custom heading"
              className="form-control mb-2"
              value={customHeading}
              onChange={(e) => setCustomHeading(e.target.value)}
            />

            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => {
                if (!customHeading.trim()) return alert("Enter a valid heading");
                addSection(customHeading.trim());
                setCustomHeading(null);
              }}
            >
              Add Custom Section
            </button>
          </div>
        )}

        <label className="fw-bold mt-3">➕ Add New Section</label>
        <select
          className="form-control"
          onChange={(e) => {
            const value = e.target.value;

            if (value === "CUSTOM") setCustomHeading("");
            else if (value !== "") addSection(value);

            e.target.value = "";
          }}
        >
          <option value="">Select Section Heading</option>
          {presetHeadings.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
          <option value="CUSTOM">CUSTOM</option>
        </select>
{/* PDF UPLOAD */}
<h4 className="mt-4">Required Documents (PDF 50–100KB)</h4>

<label>University Approval Letter</label>
<input
  type="file"
  ref={universityApprovalRef}
  className="form-control"
  onChange={(e) =>
    validateAndSetPdf(
      "universityApproval",
      e.target.files[0],
      universityApprovalRef
    )
  }
/>


<label className="mt-2">Ethical Committee (IEC) Clearance</label>
<input
  type="file"
  ref={iecClearanceRef}
  className="form-control"
  onChange={(e) =>
    validateAndSetPdf(
      "iecClearance",
      e.target.files[0],
      iecClearanceRef
    )
  }
/>


<label className="mt-2">
  No Objection Certificate (NOC)
<button
  type="button"
  className="btn btn-sm btn-outline-primary download-template-btn ms-2"
  onClick={() => downloadTemplate("noc")}
>
  ⬇ Download Template
</button>

</label>
<input
  type="file"
  ref={nocRef}
  className="form-control"
  onChange={(e) =>
    validateAndSetPdf("noc", e.target.files[0], nocRef)
  }
/>

<label className="mt-2">Submission of Final Results</label>
<input
  type="file"
  ref={finalResultsRef}
  className="form-control"
  onChange={(e) =>
    validateAndSetPdf(
      "finalResults",
      e.target.files[0],
      finalResultsRef
    )
  }
/>


<label className="mt-2">
  Licensing Agreement
  <button
    type="button"
 className="btn btn-sm btn-outline-primary download-template-btn ms-2"
    onClick={() => downloadTemplate("license")}
  >
    Download Template
  </button>
</label>
<input
  type="file"
  ref={licensingAgreementRef}
  className="form-control"
  onChange={(e) =>
    validateAndSetPdf(
      "licensingAgreement",
      e.target.files[0],
      licensingAgreementRef
    )
  }
/>

<small className="text-muted">
  Exactly 5 PDFs • Size: 50KB–100KB
</small>
{/* ----------------------------------------
    ACCEPTANCE & DIGITAL SIGNATURE
---------------------------------------- */}
<h4 className="mt-4">Declaration & Approval</h4>
<div className="form-check mt-2">
  <input
    className="form-check-input custom-checkbox"
    type="checkbox"
    id="acceptTerms"
    checked={acceptTerms}
    onChange={(e) => setAcceptTerms(e.target.checked)}
  />
  <label className="form-check-label" htmlFor="acceptTerms">
    <strong>Acceptance of Terms</strong><br />
    I hereby declare that I have carefully read, fully understood,
    and unconditionally accept all terms and conditions outlined in
    this agreement.
  </label>
</div>

<div className="form-check mt-3">
  <input
    className="form-check-input custom-checkbox success"
    type="checkbox"
    id="digitalSignature"
    checked={digitalSignature}
    onChange={(e) => setDigitalSignature(e.target.checked)}
  />
  <label className="form-check-label" htmlFor="digitalSignature">
    <strong>Digital Signature Approval</strong><br />
    By clicking this checkbox and applying my digital signature,
    I confirm my identity, authorize this action as legally binding
    authentication, and approve the terms without reservation.
  </label>
</div>

{/* 🔹 Scoped internal CSS */}
<style>
{`
  .custom-checkbox {
    width: 18px;
    height: 18px;
    background-color: transparent !important; /* no fill before check */
    border: 2px solid #6c757d;                 /* visible border */
    cursor: pointer;
  }

  .custom-checkbox:checked {
    background-color: #0d6efd !important;      /* blue fill */
    border-color: #0d6efd;
  }

  .custom-checkbox.success:checked {
    background-color: #0d6efd !important;      /* green fill */
    border-color: #0d6efd;
  }

  .custom-checkbox:focus {
    box-shadow: 0 0 0 0.2rem rgba(13,110,253,.25);
  }
`}
</style>


        <button type="submit" className="btn btn-primary mt-4">
          Submit
        </button>
      </form>

      <hr />

      {/* ----------------------------------------
          SHOW ALL PAPERS
      ---------------------------------------- */}
     <h3>Approved Research Papers</h3>

<ul className="list-group">
  {papers.map((p) => (
    <li key={p.id} className="list-group-item mb-3">
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{p.title}</h5>

        <Link
  to={`/customer/research/${p.id}`}
  className="btn btn-outline-secondary btn-sm"
>
  View Full Research
</Link>

      </div>

      <p className="mt-2 mb-0">
        <b>Status:</b>{" "}
        <span style={{ color: "green", fontWeight: "bold" }}>
          APPROVED
        </span>
      </p>
    </li>
  ))}
</ul>

    </div>
  );
};

export default Research;
